/**
 * Whether the person is actually looking at the app right now.
 *
 * "The route is open" is not the same thing: the tab can be in the
 * background, the desktop window can be hidden in the tray or sitting behind
 * another app, and in every one of those cases a message that lands in the
 * open conversation has not been seen. Treating it as read cleared the
 * notification before the person ever knew it existed; treating it as
 * on-screen suppressed the popup that would have told them.
 */
export function isAttending(): boolean {
  return document.visibilityState === "visible" && document.hasFocus();
}

/**
 * Calls `listener` whenever attention returns — the window regains focus or
 * the tab becomes visible and focused. Returns an unsubscribe.
 */
export function onAttention(listener: () => void): () => void {
  const check = () => {
    if (isAttending()) listener();
  };
  window.addEventListener("focus", check);
  document.addEventListener("visibilitychange", check);
  return () => {
    window.removeEventListener("focus", check);
    document.removeEventListener("visibilitychange", check);
  };
}

/** The path a destination points at, without its `?message=`-style query. */
export function pathOf(destination: string): string {
  const query = destination.indexOf("?");
  return query === -1 ? destination : destination.slice(0, query);
}

/** Whether `pathname` is the screen `destination` points at. */
export function isShowing(pathname: string, destination: string): boolean {
  const path = pathOf(destination);
  return pathname === path || pathname.startsWith(`${path}/`);
}
